import { Star, ExternalLink } from "lucide-react";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { useSiteT } from "../hooks/useSiteT";

export default function TrustpilotWidget({ variant = "section" }) {
    const { settings } = useSiteSettings();
    const { t } = useSiteT();
    const rating = settings.trustpilot_rating || 4.9;
    const count = settings.trustpilot_count || 320;
    if (variant === "mini") {
        return (
            <a
                href="https://www.trustpilot.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="trustpilot-mini-badge"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/10 hover:border-white/20 hover:bg-white/[0.04] transition-colors"
            >
                <span className="font-display font-bold text-[#00b67a] text-sm">
                    ★ Trustpilot
                </span>
                <span className="font-mono text-xs text-foreground/85">
                    {rating}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {count}+ {t("tp.reviews")}
                </span>
            </a>
        );
    }

    return (
        <section
            data-testid="trustpilot-section"
            className="py-16 md:py-20 border-y border-white/5 bg-white/[0.01]"
        >
            <div className="container-skala">
                <div className="grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-5 space-y-3">
                        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary">
                            {t("tp.verified")}
                        </p>
                        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                            {t("tp.rated1")}{" "}
                            <span className="text-[#00b67a]">{t("tp.excellent")}</span>{" "}
                            {t("tp.rated2")}
                        </h2>
                        <p className="text-muted-foreground text-sm md:text-base max-w-md">
                            {t("tp.sub")}
                        </p>
                    </div>

                    <div className="md:col-span-7">
                        <div className="grid sm:grid-cols-3 gap-3">
                            <div className="bg-card border border-white/[0.08] rounded-2xl p-5">
                                <div className="flex items-center gap-1 text-[#00b67a] mb-3">
                                    {[...Array(5)].map((_, i) => (
                                        <Star
                                            key={i}
                                            className="w-4 h-4 fill-current"
                                        />
                                    ))}
                                </div>
                                <div className="flex items-baseline gap-2 mb-1">
                                    <span className="font-display font-black text-4xl">
                                        {rating}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                        / 5
                                    </span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {t("tp.avg")}
                                </p>
                            </div>

                            <div className="bg-card border border-white/[0.08] rounded-2xl p-5">
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
                                    {t("tp.verified")}
                                </div>
                                <div className="font-display font-black text-4xl mb-1">
                                    {count}+
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {t("tp.on")}
                                </p>
                            </div>

                            <a
                                href="https://www.trustpilot.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-[#00b67a]/10 hover:bg-[#00b67a]/15 border border-[#00b67a]/30 rounded-2xl p-5 transition-colors group flex flex-col justify-between"
                                data-testid="trustpilot-cta-link"
                            >
                                <Star className="w-6 h-6 text-[#00b67a] fill-[#00b67a]" />
                                <div>
                                    <div className="font-display font-bold text-sm mb-0.5">
                                        {t("tp.read")}
                                    </div>
                                    <span className="inline-flex items-center gap-1 text-xs text-[#00b67a]">
                                        Trustpilot
                                        <ExternalLink className="w-3 h-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </span>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
